// ./src/pages/private/PrintSubmissionPage/PrintSubmissionPage.tsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Stack,
  Group,
  Checkbox,
  Alert,
  Center,
  Loader,
  Text,
} from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { PrintLayout } from "@/components/layouts";
import { PrintableSubmission } from "@/components/features";
import { AppIcon } from "@/components/generic";
import { useSubmissions, useForms } from "@/hooks";
import { useApp } from "@/contexts";

export const PrintSubmissionPage: React.FC<PageProps> = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const { user, isAuthenticated } = useApp();

  // Print options state
  const [showMetadata, setShowMetadata] = useState(false);
  const [showFullCreditCard, setShowFullCreditCard] = useState(false);

  // Determine if logo should be shown (hide for Business plan)
  const currentPlan = user?.account?.currentPlan;
  const showLogo = currentPlan !== "business";

  // Load submission data
  const {
    submission,
    loading: submissionLoading,
    lastError: submissionError,
    get: getSubmission,
  } = useSubmissions({
    autoLoad: false,
    isAuthenticated,
  });

  // Load form data
  const {
    form,
    loading: formLoading,
    lastError: formError,
    get: getForm,
  } = useForms({
    autoLoad: false,
    isAuthenticated,
  });

  // Load submission on mount
  useEffect(() => {
    if (submissionId && isAuthenticated) {
      getSubmission(submissionId);
    }
  }, [submissionId, isAuthenticated, getSubmission]);

  // Load form when submission is loaded
  useEffect(() => {
    if (submission?.formId) {
      getForm(submission.formId);
    }
  }, [submission?.formId, getForm]);

  // Combined loading state
  const isLoading = submissionLoading || formLoading;

  // Combined error
  const error = submissionError || formError;

  // Check if we have all required data
  const hasData = submission && form && form.formFields;

  // Handle checkbox changes
  const handleMetadataChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setShowMetadata(event.currentTarget.checked);
    },
    []
  );

  const handleCreditCardChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setShowFullCreditCard(event.currentTarget.checked);
    },
    []
  );

  // Options panel content
  const optionsPanel = (
    <Group gap="xl">
      <Checkbox
        label="Include submission metadata"
        description="IP address, user agent, referrer"
        checked={showMetadata}
        onChange={handleMetadataChange}
      />
      <Checkbox
        label="Show full credit card numbers"
        description="Display unmasked card numbers"
        checked={showFullCreditCard}
        onChange={handleCreditCardChange}
      />
    </Group>
  );

  // Loading state
  if (isLoading) {
    return (
      <PrintLayout title="Loading..." showLogo={showLogo}>
        <Center py="xl">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading submission...</Text>
          </Stack>
        </Center>
      </PrintLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <PrintLayout title="Error" showLogo={showLogo}>
        <Alert
          color="red"
          title="Failed to load submission"
          icon={<AppIcon iconName="AlertCircle" size={16} />}
        >
          {error}
        </Alert>
      </PrintLayout>
    );
  }

  // No submission found
  if (!submissionId) {
    return (
      <PrintLayout title="Error" showLogo={showLogo}>
        <Alert
          color="yellow"
          title="No submission specified"
          icon={<AppIcon iconName="AlertTriangle" size={16} />}
        >
          Please provide a submission ID in the URL.
        </Alert>
      </PrintLayout>
    );
  }

  // Submission not found or still loading form
  if (!hasData) {
    return (
      <PrintLayout title="Loading..." showLogo={showLogo}>
        <Center py="xl">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading form details...</Text>
          </Stack>
        </Center>
      </PrintLayout>
    );
  }

  return (
    <PrintLayout
      title={`Print: ${form.name || "Submission"}`}
      subtitle={`Submission ID: ${submission.id}`}
      showLogo={showLogo}
      optionsPanel={optionsPanel}
    >
      <PrintableSubmission
        submission={submission}
        form={form}
        formFields={form.formFields || []}
        showMetadata={showMetadata}
        showFullCreditCard={showFullCreditCard}
        showLogo={showLogo}
      />
    </PrintLayout>
  );
};
